/**
 * generateEmbedCode.ts
 * Pure utility to generate iframe embed snippet for accessibility passport widget
 */

export type EmbedMode = "iframe";

export interface GenerateEmbedArgs {
  baseUrl: string;
  structureId: string;
  portal?: string;
  theme?: string;
  language?: string;
  size?: string;
  version?: string;
  heightPx?: number;
  responsive?: boolean;
  autoHeight?: boolean;
  iframeId?: string;
}

/**
 * Generates an HTML embed snippet (iframe + optional auto-height script)
 * for the accessibility passport widget.
 */
export function generateEmbedCode(args: GenerateEmbedArgs): string {
  const {
    baseUrl,
    structureId,
    portal,
    theme,
    language,
    size,
    version,
    heightPx = 760,
    responsive = true,
    autoHeight = false,
    iframeId = "travelin-passport-iframe",
  } = args;

  // Build query string with only non-sensitive params
  const params = new URLSearchParams();
  params.set("structureId", structureId);
  if (portal) params.set("portal", portal);
  if (theme) params.set("theme", theme);
  if (language) params.set("language", language);
  if (size) params.set("size", size);
  if (version) params.set("v", version);

  const embedUrl = `${baseUrl}/embed/passport?${params.toString()}`;

  // Build iframe style
  const widthStyle = responsive ? "width:100%;" : "";
  const heightStyle = `height:${heightPx}px;`;
  const baseStyle = "border:0;border-radius:12px;overflow:hidden;";
  const iframeStyle = `${widthStyle}${heightStyle}${baseStyle}`;

  // Generate iframe HTML
  let snippet = `<iframe id="${iframeId}" src="${embedUrl}"
  style="${iframeStyle}"
  loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Passaporto di accessibilità"></iframe>`;

  // Add auto-height script if requested
  if (autoHeight) {
    snippet += `
<script>
(function() {
  var iframe = document.getElementById('${iframeId}');
  if (!iframe) return;
  
  window.addEventListener('message', function(event) {
    // Security: check origin matches baseUrl
    var allowedOrigin = '${new URL(baseUrl).origin}';
    if (event.origin !== allowedOrigin) return;
    
    // Handle height message from iframe
    if (event.data && event.data.type === 'travelin-passport-height') {
      var height = event.data.height;
      if (typeof height === 'number' && height > 0) {
        iframe.style.height = height + 'px';
      }
    }
  }, false);
})();
</script>`;
  }

  return snippet;
}
